"use client";

import { useEffect, useState, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { DropdownMenuField } from './dropdown-menu-field';
import type { FormOption } from '@/lib/camunda/form-data-service';
import { useFormModeGuard } from './use-form-mode-guard';

interface EntityDropdownFieldProps {
  name: string;
  label: string;
  fetcher: (id: string) => Promise<FormOption | null>;
  disabled?: boolean;
  required?: boolean;
}

interface EntityOptionState {
  entityId: string;
  loading: boolean;
  option: FormOption | null;
}

export function EntityDropdownField({ name, label, fetcher, disabled, required }: EntityDropdownFieldProps) {
  const { sideEffectsAllowed } = useFormModeGuard(disabled);
  const { watch, setValue } = useFormContext();
  const entityId = watch(name);
  const normalizedEntityId = entityId != null && entityId !== '' ? String(entityId).trim() : '';
  const [optionState, setOptionState] = useState<EntityOptionState>({
    entityId: "",
    loading: false,
    option: null,
  });
  const option = optionState.entityId === normalizedEntityId ? optionState.option : null;
  const loading = sideEffectsAllowed && Boolean(normalizedEntityId)
    ? optionState.entityId === normalizedEntityId
      ? optionState.loading
      : true
    : false;
  
  const fetcherRef = useRef(fetcher);
  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  useEffect(() => {
    if (!sideEffectsAllowed) return;

    if (!normalizedEntityId) {
      setValue(`${name}_label`, undefined, { shouldValidate: false, shouldDirty: false });
      return;
    }

    let isMounted = true;
    const fetchOption = async () => {
      try {
        const fetchedOption = await fetcherRef.current(normalizedEntityId);
        if (isMounted) {
          const normalizedOption = fetchedOption
            ? { ...fetchedOption, value: String(fetchedOption.value).trim() }
            : null;
          setOptionState({ entityId: normalizedEntityId, loading: false, option: normalizedOption });
          setValue(`${name}_label`, normalizedOption?.label ?? undefined, {
            shouldValidate: false,
            shouldDirty: false,
          });
        }
      } catch (error) {
        console.error(`Error fetching data for ${name} with ID ${normalizedEntityId}:`, error);
        if (isMounted) {
          setOptionState({ entityId: normalizedEntityId, loading: false, option: null });
          setValue(`${name}_label`, undefined, { shouldValidate: false, shouldDirty: false });
        }
      }
    };

    void fetchOption();

    return () => { isMounted = false; };
  }, [name, normalizedEntityId, setValue, sideEffectsAllowed]);

  const placeholder = loading ? "Cargando..." : "No disponible";
  const options = option ? [option] : [];

  return (
    <DropdownMenuField
      name={name}
      label={label}
      options={options}
      placeholder={placeholder}
      disabled={!sideEffectsAllowed || loading}
      required={required}
    />
  );
}
